import { businessesSearchQueryOptions } from '@/api/queries/businesses';
import { menuItemsSearchQueryOptions } from '@/api/queries/menu-items';
import { usersSearchQueryOptions } from '@/api/queries/users';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { dropdownOffsets } from '../utils/dropdown-offsets';

export function useDiscoverSearch() {
  const insets = useSafeAreaInsets();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchBarRef = useRef<View>(null);
  const [dropdownTop, setDropdownTop] = useState<number>(120);
  const [searchBarBottom, setSearchBarBottom] = useState<number>(0);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRefocusingRef = useRef(false);

  const debouncedQuery = useDebouncedValue(searchQuery, 300);

  const { data: searchResults, isLoading: isSearchLoading } = useQuery(
    businessesSearchQueryOptions({
      query: debouncedQuery.trim(),
      city: 'Bloomington',
      state: 'IN',
      per_page: 5,
    })
  );

  const { data: userSearchResults, isLoading: isUserSearchLoading } = useQuery(
    usersSearchQueryOptions({
      query: debouncedQuery.trim(),
      per_page: 5,
    })
  );

  const { data: menuItemSearchResults, isLoading: isMenuItemSearchLoading } = useQuery(
    menuItemsSearchQueryOptions({
      query: debouncedQuery.trim(),
      per_page: 5,
    })
  );

  const mappedResults = useMemo(() => {
    if (!searchResults) return null;
    return searchResults;
  }, [searchResults]);

  const mappedUserResults = useMemo(() => {
    if (!userSearchResults) return null;
    return userSearchResults;
  }, [userSearchResults]);

  const showTypeahead = isSearchFocused && debouncedQuery.trim().length >= 2;
  const hasBusinessResults = !!(mappedResults && mappedResults.items.length > 0);
  const hasUserResults = !!(mappedUserResults && mappedUserResults.items.length > 0);
  const hasMenuItemResults = !!(menuItemSearchResults && menuItemSearchResults.items.length > 0);
  const hasResults = hasBusinessResults || hasUserResults || hasMenuItemResults;
  const isLoading = isSearchLoading || isUserSearchLoading || isMenuItemSearchLoading;

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleSearchBarLayout = useCallback(() => {
    searchBarRef.current?.measureInWindow((_x, y, _width, height) => {
      const offsets = dropdownOffsets(y, height, insets.top);
      setDropdownTop(offsets.dropdownTop);
      setSearchBarBottom(offsets.searchBarBottom);
    });
  }, [insets.top]);

  const handleSearchFocus = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    isRefocusingRef.current = true;
    setIsSearchFocused(true);
    setTimeout(() => {
      searchBarRef.current?.measureInWindow((_x, y, _width, height) => {
        const offsets = dropdownOffsets(y, height, insets.top);
        setDropdownTop(offsets.dropdownTop);
        setSearchBarBottom(offsets.searchBarBottom);
      });
      setTimeout(() => {
        isRefocusingRef.current = false;
      }, 300);
    }, 100);
  }, [insets.top]);

  const handleSearchBlur = useCallback(() => {
    blurTimeoutRef.current = setTimeout(() => {
      if (!isRefocusingRef.current) {
        setIsSearchFocused(false);
      }
      blurTimeoutRef.current = null;
    }, 250);
  }, []);

  return {
    searchQuery,
    isSearchFocused,
    searchBarRef,
    dropdownTop,
    searchBarBottom,
    debouncedQuery,
    showTypeahead,
    isLoading,
    hasResults,
    hasBusinessResults,
    hasUserResults,
    hasMenuItemResults,
    mappedResults,
    mappedUserResults,
    menuItemSearchResults,
    handleSearchChange,
    handleSearchBarLayout,
    handleSearchFocus,
    handleSearchBlur,
    setIsSearchFocused,
  };
}
